import { Inspection } from '@stormwater-software/lib-inspection-templates';
import xhrService from '../xhrService';
import { Finding } from './finding';
import db from '../../db/db';
import { withOfflineFallback } from '../offlineFallback';
const baseUrl = '/api/projects';

export type ProjectInspection = Omit<Inspection, 'id'> & {
  id: number;
  findings: Array<Finding>;
  pageNumber?: string | number | null;
  pageSize?: number;
  project?: any;
  findingDetails?: any;
  /** SW2-2248 — true while a review raised for this inspection is still pending. */
  pending_review?: boolean;
};

export type SlimProjectInspection = {
  id: number;
  created_date?: Date;
  certification_date?: Date;
  compliance_date?: Date;
  inspection_template?: {
    id?: number;
    name?: string;
    inspectionTemplateType?: { id: number };
  };
  /** SW2-2248 — true while a review raised for this inspection is still pending. */
  pending_review?: boolean;
};

export const projectInspectionApi = {
  index: async (
    id: number,
    pageNumber?: string | number | null,
    pageSize?: number,
    inspection_template_type_id?: number,
    inspectionType?: string,
    inspection_template_type?: string,
    inspection_type?: string,
  ): Promise<any> => {
    const params: {
      p?: string | number | null;
      pageSize?: number;
      inspection_template_type_id?: number;
      inspectionType?: string;
      inspection_template_type?: string;
      inspection_type?: string;
    } = {};

    if (pageNumber) {
      params.p = pageNumber;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (inspection_template_type_id) {
      params.inspection_template_type_id = inspection_template_type_id;
    }

    if (inspectionType) {
      params.inspectionType = inspectionType;
    }

    if (inspection_template_type) {
      params.inspection_template_type = inspection_template_type;
    }

    if (inspection_type) {
      params.inspection_type = inspection_type;
    }

    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload the inspection list was empty despite the records being cached.
    // The inner .catch that only logged and rethrew is dropped: withOfflineFallback
    // logs a failing cache read itself and then surfaces the request error.
    return withOfflineFallback<any>(
      () =>
        xhrService
          .get(`${baseUrl}/${id}/inspections`, { params })
          .then((response) => response.data),
      async () => {
        const data = await db.inspections
          .where('project_id')
          .equals(Number(id))
          .sortBy('inspectionNumber')
          .then((inspections: any) => inspections.reverse());

        // A downloaded project with no inspections yet is a legitimate empty
        // answer, not a cache miss — an earlier version returned null here,
        // which made an empty offline inspection list throw instead of render.
        // `onlyWhenUnreachable` below is what stops this from masking a real
        // server error.
        return {
          pageData: data,
          paginationState: { currentPage: 1, pageSize: 1, numModels: 1 },
        };
      },
      { onlyWhenUnreachable: true },
    );
  },
  indexSlim: async (
    id: number,
    inspection_template_type_id?: number,
  ): Promise<any> => {
    const params: {
      slim: number;
      inspection_template_type_id?: number;
    } = { slim: 1 };

    if (inspection_template_type_id) {
      params.inspection_template_type_id = inspection_template_type_id;
    }

    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload the inspection list was empty despite the records being cached.
    // The inner .catch that only logged and rethrew is dropped: withOfflineFallback
    // logs a failing cache read itself and then surfaces the request error.
    return withOfflineFallback<any>(
      () =>
        xhrService
          .get(`${baseUrl}/${id}/inspections`, { params })
          .then((response) => response.data),
      async () => {
        const data = await db.inspections
          .where('project_id')
          .equals(Number(id))
          .sortBy('inspectionNumber')
          .then((inspections: any) => inspections.reverse());

        // A downloaded project with no inspections yet is a legitimate empty
        // answer, not a cache miss — an earlier version returned null here,
        // which made an empty offline inspection list throw instead of render.
        // `onlyWhenUnreachable` below is what stops this from masking a real
        // server error.
        return {
          pageData: data,
          paginationState: { currentPage: 1, pageSize: 1, numModels: 1 },
        };
      },
      { onlyWhenUnreachable: true },
    );
  },
  indexIds: (id: number, inspection_template_type_id?: number): any => {
    const params: { inspection_template_type_id?: number } = {};

    if (inspection_template_type_id) {
      params.inspection_template_type_id = inspection_template_type_id;
    }

    return xhrService
      .get(`${baseUrl}/${id}/inspections/Ids`, { params })
      .then((response) => response.data);
  },
  getInspectionTypesByProject: (projectId: number): Promise<string[]> =>
    xhrService
      .get(`${baseUrl}/${projectId}/inspections/inspection-types`)
      .then((response) => response.data.data),
};

export default projectInspectionApi;
